import type { ReactNode } from "react";

import { Button } from "./Button";

/** Image framing. `portrait` is taller and top-anchored so a standing
 *  figure's head is never cropped; `square` is the default content crop. */
const IMAGE_ASPECT = {
  square: "aspect-square object-center",
  portrait: "aspect-[4/5] object-top",
} as const;

interface CardProps {
  eyebrow?: string;
  title: string;
  image?: { src: string; alt: string };
  imageAspect?: keyof typeof IMAGE_ASPECT;
  children?: ReactNode;
  cta?: { label: string; href: string; external?: boolean };
}

/**
 * A content card: optional image, optional eyebrow, title, optional body,
 * optional CTA.
 *
 * The body is only rendered when there is something to put in it, and the
 * CTA carries `mt-auto` (not `flex-1` on the body), so a childless card -
 * the Akhand Path dates, the Gita Jayanti card, the gallery covers - does
 * not pay a margin for a zero-height div, and every CTA on one grid row
 * still sits on the same baseline. Same idiom as Field and UpcomingBlock.
 */
export function Card({
  eyebrow,
  title,
  image,
  imageAspect = "square",
  children,
  cta,
}: CardProps) {
  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-hairline bg-card shadow-soft transition duration-300 hover:-translate-y-1 hover:shadow-lift">
      {image ? (
        <div className="overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${IMAGE_ASPECT[imageAspect]}`}
            loading="lazy"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-6">
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h3
          className={`font-display text-xl text-ink${eyebrow ? " mt-2" : ""}`}
        >
          {title}
        </h3>
        {children ? (
          <div className="mt-3 space-y-2 text-sm leading-relaxed text-ink-soft">
            {children}
          </div>
        ) : null}
        {cta ? (
          <div className="mt-auto pt-5">
            <Button
              href={cta.href}
              external={cta.external}
              variant="secondary"
              className="after:absolute after:inset-0"
            >
              {cta.label}
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  );
}
